import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const leLavandou: ChauffeurPage = {
  slug: "le-lavandou",
  title: "Chauffeur privé & VTC au Lavandou",
  subtitle:
    "Chauffeur privé au Lavandou : douze plages, embarcadère pour les îles d'Hyères et transferts vers Toulon-Hyères et Nice.",
  keywords: [
    "chauffeur privé le lavandou",
    "vtc le lavandou",
    "transfert aéroport le lavandou",
    "chauffeur lavandou embarcadère",
  ],
  hero: {
    title: "Chauffeur privé & VTC au Lavandou",
    subtitle:
      "Douze plages, le front de mer et l'embarcadère des îles : un chauffeur pour tout le séjour, 24h/24.",
    keywords: [
      "VTC Le Lavandou",
      "chauffeur front de mer",
      "embarcadère Porquerolles",
      "transfert aéroport Toulon-Hyères",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC au Lavandou",
    badges: [
      "Aéroport Toulon-Hyères ~35 min",
      "Embarcadère des îles",
      "Chauffeurs FR/EN",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur au Lavandou",
    items: [
      {
        title: "Aéroport le plus proche",
        description:
          "Toulon-Hyères à environ 35 min ; Nice Côte d'Azur reste possible à près de 1h40 pour les vols internationaux.",
      },
      {
        title: "Douze plages à desservir",
        description:
          "De Saint-Clair à Pramousquier, dépose au plus près du sable et reprise à l'heure convenue.",
      },
      {
        title: "Départ vers les îles",
        description:
          "Liaison avec l'embarcadère pour Porquerolles, Port-Cros et Le Levant, bagages portés jusqu'au quai.",
      },
      {
        title: "Service noté 5,0/5",
        description:
          "Chauffeurs ponctuels et discrets, basés sur la Côte d'Azur, notés 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur au Lavandou",
    services: [
      {
        title: "Transfert aéroport de Toulon-Hyères",
        text: "Environ 35 min de trajet, suivi de vol inclus, depuis et vers l'aéroport de Toulon-Hyères.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Pour les longs-courriers, liaison vers Nice Côte d'Azur en près de 1h40, van premium sur demande.",
      },
      {
        title: "Navette embarcadère & plages",
        text: "Dépose au port pour les navettes des îles d'Hyères et allers-retours vers les douze plages.",
      },
      {
        title: "Gare TGV de Toulon",
        text: "Transfert vers la gare de Toulon pour les TGV Paris, avec marge sur le trafic du littoral.",
      },
      {
        title: "Mise à disposition & longue distance",
        text: "Chauffeur à la journée dans le Var, ou trajets vers Saint-Tropez, Cannes, Nice et l'Italie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Front de mer du Lavandou",
        description: "Promenade, quais et centre de la station.",
      },
      {
        name: "Embarcadère des îles",
        description:
          "Départ des navettes vers Porquerolles, Port-Cros et Le Levant.",
      },
      {
        name: "Plage de Saint-Clair",
        description: "Grande plage à l'est de la station.",
      },
      {
        name: "Cavalière & Pramousquier",
        description: "Plages de la corniche vers Cavalaire.",
      },
      {
        name: "Aéroport de Toulon-Hyères",
        description: "L'aéroport le plus proche, à environ 35 min.",
      },
      {
        name: "Bormes-les-Mimosas",
        description: "Village fleuri voisin, à quelques minutes.",
      },
    ],
  },
  generic: {
    heading: "Le Lavandou, douze plages et le large des îles",
    subheading:
      "Une station étirée le long du littoral, où chaque plage a sa propre entrée et son propre parking.",
    paragraphs: [
      "Le Lavandou aligne douze plages, de Saint-Clair à Pramousquier, séparées par une corniche et des accès parfois étroits. Un chauffeur privé vous dépose au plus près de celle que vous visez et revient à l'heure dite, sans que vous ayez à tourner pour un stationnement qui se raréfie dès les beaux jours.",
      "Le port abrite l'embarcadère des îles d'Hyères : nous vous y déposons avec vos bagages pour les navettes de Porquerolles, Port-Cros et Le Levant, et nous vous récupérons au retour. Côté aéroports, Toulon-Hyères est le plus proche, à environ 35 minutes, tandis que Nice Côte d'Azur se rejoint en près de 1h40 pour les vols internationaux ; la gare TGV de Toulon complète l'accès vers Paris.",
      "Pour le reste du séjour, la mise à disposition confie le même chauffeur à vos journées : allers-retours plages, dîners à Bormes ou dans le golfe, excursions vers Saint-Tropez, avec des forfaits fixes annoncés à l'avance et un devis immédiat.",
    ],
    bullets: [
      "Dépose à chaque plage",
      "Liaison embarcadère des îles",
      "Forfaits fixes & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver un chauffeur au Lavandou",
    steps: [
      "Indiquez votre vol, votre navette pour les îles ou votre adresse au Lavandou.",
      "Nous conseillons le véhicule selon les bagages, le groupe et la distance.",
      "Le chauffeur suit le vol ou l'horaire du bateau et cale le départ.",
      "Transfert direct ou mise à disposition pour les plages et les sorties.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Le Lavandou", undefined, "au Lavandou"),
    {
      q: "Quel aéroport est le plus proche du Lavandou ?",
      a: "L'aéroport de Toulon-Hyères, à environ 35 minutes, est le plus proche. Pour les vols internationaux, Nice Côte d'Azur se situe à près de 1h40 par l'A57.",
    },
    {
      q: "Déposez-vous à l'embarcadère pour Porquerolles ?",
      a: "Oui. Nous vous conduisons au port du Lavandou pour les navettes des îles d'Hyères — Porquerolles, Port-Cros, Le Levant — et vous récupérons au retour, bagages compris.",
    },
    {
      q: "Assurez-vous les transferts vers la gare TGV de Toulon ?",
      a: "Oui, la gare de Toulon relie Paris en TGV ; nous programmons le départ sur votre horaire de train et anticipons le trafic côtier en saison.",
    },
    {
      q: "Combien de temps à l'avance réserver ?",
      a: "En été, réservez le plus tôt possible : les plages et l'embarcadère sont très fréquentés. Les demandes de dernière minute sont traitées selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, ils sont bilingues français/anglais et habitués à une clientèle internationale de vacanciers et de plaisanciers.",
    },
    {
      q: "Quels véhicules pour une famille avec bagages ?",
      a: "Un van premium 7 places convient aux familles et aux bagages ; les berlines Classe E ou Classe S sont idéales pour deux à trois voyageurs.",
    },
    {
      q: "Faites-vous les longues distances depuis Le Lavandou ?",
      a: "Oui : Saint-Tropez, Cannes, Nice, Monaco, Marseille et l'Italie, ainsi que la desserte depuis Cannes ou Nice vers Le Lavandou, en confort haut de gamme.",
    },
  ],
  relatedLinks: [
    {
      label: "Service de chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    {
      label: "VTC Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Service de chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Louer une voiture au Lavandou",
      href: "/location/location-voiture-le-lavandou",
    },
  ],
  cta: {
    title: "Votre chauffeur privé au Lavandou",
    text: "Transfert aéroport, embarcadère des îles ou tournée des plages : réservez votre chauffeur, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé au Lavandou — VTC & transfert aéroport",
    description:
      "Chauffeur privé au Lavandou : transfert aéroport de Toulon-Hyères (~35 min) et de Nice, gare TGV de Toulon, douze plages et îles d'Hyères.",
    canonical: "/chauffeur/le-lavandou",
  },
};

export default leLavandou;
